/**
 * For component/feature-specific monorail events, use the `useMonorail` hook in conjunction with
 * the `produceMonorailEvent` method. Feature-specific monorail eventing **should not** be handled by
 * the root-level provider + hook.
 *
 * If you have 2-3 features using a custom version of the `productMonorailEvent` method, consider
 * creating a custom hook to share that functionality across your features.
 */
import {Monorail} from '@shopify/monorail';
import {MonorailRequestError} from '@shopify/monorail/lib/producers/producer-errors';
import type {FunctionComponent} from 'preact';
import {useCallback, useEffect, useMemo, useState} from 'preact/hooks';

import {useRootProvider} from '~/foundation/RootProvider/hooks';
import type {LoginWithShopSdkPageName} from '~/types/analytics';
import {AbstractShopJSError} from '~/utils/errors';
import {isoWindow} from '~/utils/window';

import {useBugsnag} from '../Bugsnag/hooks';
import {useOpenTelemetry} from '../OpenTelemetry/hooks';
import {groupOpentelError} from '../OpenTelemetry/utils';

import {MonorailContext} from './context';
import type {
  AnalyticsData,
  MonorailProviderProps,
  ProduceMonorailEventParams,
  TrackFeatureInitializationParams,
  TrackPageImpressionParams,
  TrackShopPayModalStateChangeParams,
  TrackUserActionParams,
} from './types';
import {getStorefrontAnalytics, getTrekkieAttributes} from './utils';

const DEFAULT_FLOW_VERSION = 'unspecified';
const TRACKED_PAGE_IMPRESSIONS = new Set<LoginWithShopSdkPageName>();
export const PRODUCER =
  // eslint-disable-next-line no-process-env
  process.env.NODE_ENV === 'production'
    ? Monorail.createHttpProducer({production: true})
    : Monorail.createLogProducer({debugMode: true});

export const MonorailProvider: FunctionComponent<MonorailProviderProps> = ({
  children,
  monorailProps = {},
}) => {
  const {recordCounter} = useOpenTelemetry();
  const {devMode} = useRootProvider();
  const [analyticsData] = useState<AnalyticsData>({
    ...monorailProps,
    flowVersion: monorailProps.flowVersion ?? DEFAULT_FLOW_VERSION,
  });
  const {notify} = useBugsnag();

  useEffect(() => {
    return () => {
      TRACKED_PAGE_IMPRESSIONS.clear();
    };
  }, []);

  const produceMonorailEvent = useCallback(
    ({event, onError, trekkieAttributes}: ProduceMonorailEventParams) => {
      if (devMode) {
        return;
      }

      const isUsefulError = (error: any) => {
        return (
          !(error instanceof MonorailRequestError) &&
          !error?.message?.includes('Invalid agent:')
        );
      };

      if (trekkieAttributes && !Object.keys(trekkieAttributes).length) {
        // If trekkie attributes are provided but the object is empty then we don't want to send the event
        // since we know it will not have a valid schema
        onError?.({message: 'trekkie attributes are empty'});
        return;
      }

      event.payload = Object.assign(event.payload, trekkieAttributes);

      PRODUCER.produce(event).catch((error) => {
        onError?.(error);

        if (isUsefulError(error)) {
          const caughtError =
            error instanceof Error
              ? error
              : new AbstractShopJSError(String(error), 'MonorailProducerError');
          notify(caughtError);

          const opentelError = groupOpentelError(caughtError);
          recordCounter('shop_js_monorail_producer_error', {
            attributes: {error: opentelError},
          });
        }
      });
    },
    [devMode, notify, recordCounter],
  );

  const trackFeatureInitialization = useCallback(
    async ({source = 'unspecified'}: TrackFeatureInitializationParams) => {
      const {
        analyticsTraceId,
        apiKey,
        checkoutToken,
        flow,
        flowVersion = DEFAULT_FLOW_VERSION,
        shopId,
      } = analyticsData;

      if (!analyticsTraceId || !flow) {
        return;
      }

      const storefrontAnalytics = getStorefrontAnalytics();
      const storefrontPageType = storefrontAnalytics?.pageType ?? '';
      const trekkieAttributes = await getTrekkieAttributes(
        'customerId',
        'isPersistentCookie',
        'path',
        'uniqToken',
        'visitToken',
      );

      const payload = {
        ...(apiKey && {apiKey}),
        ...(checkoutToken && {checkoutToken}),
        ...(shopId && {shopId}),
        ...trekkieAttributes,
        analyticsTraceId,
        flow,
        flowVersion,
        sdkVersion: '__buildVersion',
        shopPermanentDomain: isoWindow.Shopify?.shop ?? '',
        source,
        storefrontPageType,
      };

      produceMonorailEvent({
        event: {
          schemaId: 'shopify_pay_login_with_shop_sdk_feature_initialize/1.0',
          payload,
        },
      });
    },
    [analyticsData, produceMonorailEvent],
  );

  useEffect(() => {
    trackFeatureInitialization({source: analyticsData.source});
  }, [analyticsData.source, trackFeatureInitialization]);

  const trackPageImpression = useCallback(
    async ({page, shopAccountUuid}: TrackPageImpressionParams) => {
      if (TRACKED_PAGE_IMPRESSIONS.has(page)) {
        return;
      }

      const {
        analyticsTraceId,
        apiKey,
        flow,
        flowVersion = DEFAULT_FLOW_VERSION,
      } = analyticsData;

      if (!analyticsTraceId || !flow) {
        return;
      }

      TRACKED_PAGE_IMPRESSIONS.add(page);

      const storefrontAnalytics = getStorefrontAnalytics();
      const storefrontPageType = storefrontAnalytics?.pageType ?? '';
      const trekkieAttributes = await getTrekkieAttributes(
        'customerId',
        'isPersistentCookie',
        'path',
        'uniqToken',
        'visitToken',
      );

      const payload = {
        ...(apiKey && {apiKey}),
        ...(shopAccountUuid && {shopAccountUuid}),
        ...trekkieAttributes,
        analyticsTraceId,
        flow,
        flowVersion,
        pageName: page,
        sdkVersion: '__buildVersion',
        shopPermanentDomain: isoWindow.Shopify?.shop ?? '',
        storefrontPageType,
      };

      produceMonorailEvent({
        event: {
          payload,
          schemaId: 'shopify_pay_login_with_shop_sdk_page_impressions/3.3',
        },
        onError: () => {
          TRACKED_PAGE_IMPRESSIONS.delete(page);
        },
        trekkieAttributes,
      });
    },
    [analyticsData, produceMonorailEvent],
  );

  const trackUserAction = useCallback(
    ({userAction}: TrackUserActionParams) => {
      const {
        analyticsTraceId,
        apiKey,
        checkoutVersion,
        flow,
        flowVersion = DEFAULT_FLOW_VERSION,
        shopId,
      } = analyticsData;

      if (!analyticsTraceId || !flow) {
        return;
      }

      const payload = {
        ...(apiKey && {apiKey}),
        ...(checkoutVersion && {checkoutVersion}),
        ...(shopId && {shopId}),
        analyticsTraceId,
        flow,
        flowVersion,
        sdkVersion: '__buildVersion',
        shopPermanentDomain: isoWindow.Shopify?.shop ?? '',
        userAction,
      };

      produceMonorailEvent({
        event: {
          schemaId: 'shopify_pay_login_with_shop_sdk_user_actions/2.1',
          payload,
        },
      });
    },
    [analyticsData, produceMonorailEvent],
  );

  const trackShopPayModalStateChange = useCallback(
    ({currentState}: TrackShopPayModalStateChangeParams) => {
      const {
        analyticsTraceId,
        flow,
        flowVersion = DEFAULT_FLOW_VERSION,
      } = analyticsData;

      if (!analyticsTraceId || !flow) {
        return;
      }

      produceMonorailEvent({
        event: {
          schemaId: 'shop_identity_modal_state_change/1.0',
          payload: {
            analyticsTraceId,
            currentState,
            flow,
            flowVersion,
          },
        },
      });
    },
    [analyticsData, produceMonorailEvent],
  );

  const getAnalyticsData: () => Readonly<AnalyticsData> = useCallback(() => {
    return analyticsData;
  }, [analyticsData]);

  /**
   * Do not add any feature-specific events here. See comment at the top of the file.
   */

  const value = useMemo(() => {
    return {
      getAnalyticsData,
      getTrekkieAttributes,
      produceMonorailEvent,
      trackPageImpression,
      trackShopPayModalStateChange,
      trackUserAction,
    };
  }, [
    getAnalyticsData,
    produceMonorailEvent,
    trackPageImpression,
    trackShopPayModalStateChange,
    trackUserAction,
  ]);

  return (
    <MonorailContext.Provider value={value}>
      {children}
    </MonorailContext.Provider>
  );
};
